'use client';

import * as React from 'react';
import { DirectionProvider } from '@radix-ui/react-direction';
import { TooltipProvider } from './Tooltip';
import { Toaster, type ToasterProps } from './Toast';

/**
 * Strings the design system has to say on its own behalf, with no caller in sight — today just the
 * accessible name of the ✕ that dismisses a `<Dialog>` or a `<Sheet>`.
 *
 * This package is locale-agnostic on purpose (the export worker renders it with no i18n runtime), so
 * it cannot import next-intl. The app hands the translated strings down here once, next to `dir`,
 * and every component reads them through `useUILabels()`.
 */
export interface UILabels {
  /** Accessible name of the close (✕) control on dialogs and sheets. */
  close: string;
}

/** English, not Hebrew: a neutral fallback for a package that does not know the locale. */
export const DEFAULT_UI_LABELS: UILabels = { close: 'Close' };

const LabelsContext = React.createContext<UILabels>(DEFAULT_UI_LABELS);

/** The localised shared labels supplied by the nearest `<UIProvider>`. */
export function useUILabels(): UILabels {
  return React.useContext(LabelsContext);
}

export interface UIProviderProps {
  /** Must match `<html dir>`. Radix needs it to resolve `align="start"` and menu arrow keys. */
  dir?: 'rtl' | 'ltr';
  /** Global tooltip open delay. */
  tooltipDelay?: number;
  /** Mount the Toaster here (default true). Pass `false` to place it yourself. */
  toaster?: boolean | ToasterProps;
  /** Localised strings for the shared chrome (UX-42). Anything omitted falls back to English. */
  labels?: Partial<UILabels>;
  children: React.ReactNode;
}

/**
 * Wrap the app once, inside the locale layout:
 * `<UIProvider dir={locale === 'he' ? 'rtl' : 'ltr'} labels={{ close: t('common.close') }}>…</UIProvider>`
 */
export function UIProvider({ dir = 'rtl', tooltipDelay = 350, toaster = true, labels, children }: UIProviderProps) {
  const resolvedLabels = React.useMemo<UILabels>(
    () => ({ ...DEFAULT_UI_LABELS, ...(labels ?? {}) }),
    [labels],
  );
  return (
    <DirectionProvider dir={dir}>
      <LabelsContext.Provider value={resolvedLabels}>
        <TooltipProvider delayDuration={tooltipDelay} skipDelayDuration={300}>
          {children}
          {toaster ? <Toaster {...(typeof toaster === 'object' ? toaster : {})} /> : null}
        </TooltipProvider>
      </LabelsContext.Provider>
    </DirectionProvider>
  );
}
